import { Grid, IconButton, SvgIcon } from '@mui/material';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';

import StyledSearchBar from './SearchBar.styles';
import SearchBarProps from './SearchBar.types';

const SearchBar: FunctionComponent<SearchBarProps> = (
  props: SearchBarProps
): JSX.Element => {
  const {
    value,
    disabled,
    maxWidth,
    setValue,
    onChange,
    onSearch,
    ...resProps
  } = props;

  /** Translation Hook */
  const { t } = useTranslation();

  /** Input Change Handler
   * Set Input Value
   * Show Clear Icon when there is input value
   */
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value;
    setValue(inputValue);

    if (onChange) {
      onChange(event);
    }
  };

  /** Key Press Handler
   *
   * Calls `onSearch` when enter is pressed
   */
  const onKeyPressHandler = (event: React.KeyboardEvent<Element>) => {
    if (event.key === CommonConstants.ENTER_KEY && onSearch) {
      onSearch(value);
    }
  };

  /** Clears the input field and hide Clear Icon */
  const closeIconClickHandler = () => {
    setValue('');
    if (onSearch) {
      onSearch('');
    }
  };

  /** Start Adornment - Search Icon */
  const startAdornment = (
    <SvgIcon className="searchIcon">{Icons.search}</SvgIcon>
  );

  /** End Adornment - Clear Icon */
  const endAdornment = (
    <IconButton onClick={closeIconClickHandler}>
      <SvgIcon>{Icons.close}</SvgIcon>
    </IconButton>
  );

  return (
    <Grid
      container
      alignItems="center"
      flexWrap="nowrap"
      maxWidth={maxWidth ? `${maxWidth}rem` : '100%'}
      role="searchbox"
    >
      <StyledSearchBar
        size="small"
        value={value}
        onChange={onChangeHandler}
        onKeyPress={onKeyPressHandler}
        disabled={disabled}
        {...resProps}
        role="search"
        InputProps={{
          startAdornment,
          endAdornment: value && endAdornment,
        }}
      />
      <Button
        variant="outlined"
        color="secondary"
        disabled={disabled}
        onClick={onSearch ? () => onSearch(value) : () => {}}
        sx={{
          minWidth: '8rem',
          borderRadius: '0 0.4rem 0.4rem 0',
          borderColor: 'secondary.main',
          textTransform: 'capitalize',
        }}
      >
        {t('search')}
      </Button>
    </Grid>
  );
};

SearchBar.defaultProps = {
  fullWidth: true,
};

export default SearchBar;
